/**
 * DispatchStatsRow
 *
 * Dashboard stats row showing 4 key statistics:
 * - Guards on duty
 * - Patrols in progress
 * - Completed patrols (X/Y)
 * - Active alerts
 *
 * Each stat card is clickable to filter the dashboard view.
 */

import React from 'react';
import { Box, Typography, Skeleton, Tooltip, useTheme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import styles from './DispatchStatsRow.module.css';
import classNames from 'classnames';

/** Stats data structure from API */
export interface DispatchStats {
  guardsOnDuty: number;
  patrolsInProgress: number;
  patrolsCompletedToday: number;
  patrolsTotalToday: number;
  activeAlerts: number;
}

/** Stat type for click filtering */
export type StatType = 'onDuty' | 'patrolling' | 'completed' | 'alerts';

export interface DispatchStatsRowProps {
  /** Stats data to display */
  stats: DispatchStats | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message (shows error state if provided) */
  error?: string | null;
  /** Callback when a stat card is clicked */
  onStatClick?: (statType: StatType) => void;
  /** Currently active/selected stat filter */
  activeFilter?: StatType | null;
  /** Callback to retry loading after error */
  onRetry?: () => void;
  /** Compact mode — renders horizontal scrollable pills for mobile */
  compact?: boolean;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  statType: StatType;
  isActive: boolean;
  onClick?: (statType: StatType) => void;
  tooltip?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  label,
  value,
  color,
  statType,
  isActive,
  onClick,
  tooltip,
}) => {
  const card = (
    <Box
      className={classNames(styles.statCard, {
        [styles.active]: isActive,
        [styles.clickable]: !!onClick,
      })}
      onClick={() => onClick?.(statType)}
      style={{ '--stat-color': color } as React.CSSProperties}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(statType);
        }
      }}
    >
      <Box className={styles.statIcon}>{icon}</Box>
      <Box className={styles.statContent}>
        <Typography variant="h4" className={styles.statValue}>
          {value}
        </Typography>
        <Typography variant="body2" className={styles.statLabel}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
};

const StatCardSkeleton: React.FC = () => (
  <Box className={styles.statCard}>
    <Box className={styles.statIcon}>
      <Skeleton variant="circular" width={32} height={32} animation="wave" />
    </Box>
    <Box className={styles.statContent}>
      <Skeleton variant="text" width={50} height={40} animation="wave" />
      <Skeleton variant="text" width={80} height={20} animation="wave" />
    </Box>
  </Box>
);

/** Compact pill skeleton for loading state */
const CompactPillSkeleton: React.FC = () => (
  <Box className={styles.compactPill}>
    <Skeleton variant="text" width={28} height={20} animation="wave" sx={{ margin: '0 auto' }} />
    <Skeleton variant="text" width={48} height={8} animation="wave" sx={{ margin: '2px auto 0' }} />
  </Box>
);

/** Compact pill definition for building the pill list */
interface CompactPillDef {
  label: string;
  value: string | number;
  color: string;
  statType: StatType;
  isDanger?: boolean;
}

const DispatchStatsRow: React.FC<DispatchStatsRowProps> = ({
  stats,
  isLoading = false,
  error = null,
  onStatClick,
  activeFilter = null,
  onRetry,
  compact = false,
}) => {
  const theme = useTheme();

  // --- Compact mode ---
  if (compact) {
    // Loading state (compact)
    if (isLoading) {
      return (
        <Box className={styles.compactRow}>
          <CompactPillSkeleton />
          <CompactPillSkeleton />
          <CompactPillSkeleton />
          <CompactPillSkeleton />
        </Box>
      );
    }

    // Error state (compact)
    if (error) {
      return (
        <Box className={styles.compactRow}>
          <Box className={styles.errorState}>
            <WarningIcon className={styles.errorIcon} />
            <Typography variant="body2" className={styles.errorText}>
              {error}
            </Typography>
            {onRetry && (
              <Typography
                variant="body2"
                className={styles.retryLink}
                onClick={onRetry}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRetry();
                  }
                }}
              >
                Retry
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    // No data state (compact)
    if (!stats) {
      return (
        <Box className={styles.compactRow}>
          <Typography variant="body2" color="textSecondary">
            No statistics available
          </Typography>
        </Box>
      );
    }

    const completedDisplay = `${stats.patrolsCompletedToday}/${stats.patrolsTotalToday}`;

    const pills: CompactPillDef[] = [
      { label: 'On Duty', value: stats.guardsOnDuty, color: theme.palette.success.main, statType: 'onDuty' },
      { label: 'Patrolling', value: stats.patrolsInProgress, color: theme.palette.info.main, statType: 'patrolling' },
      { label: 'Completed', value: completedDisplay, color: theme.palette.grey[500], statType: 'completed' },
      {
        label: 'Alerts',
        value: stats.activeAlerts,
        color: stats.activeAlerts > 0 ? theme.palette.error.main : theme.palette.grey[500],
        statType: 'alerts',
        isDanger: stats.activeAlerts > 0,
      },
    ];

    return (
      <Box className={styles.compactRow}>
        {pills.map((pill) => (
          <Box
            key={pill.statType}
            className={classNames(styles.compactPill, {
              [styles.active]: activeFilter === pill.statType,
              [styles.compactPillDanger]: pill.isDanger,
            })}
            style={{ '--stat-color': pill.color } as React.CSSProperties}
            onClick={() => onStatClick?.(pill.statType)}
            role={onStatClick ? 'button' : undefined}
            tabIndex={onStatClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onStatClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onStatClick(pill.statType);
              }
            }}
          >
            <Box className={styles.compactPillValue}>{pill.value}</Box>
            <Box className={styles.compactPillLabel}>{pill.label}</Box>
          </Box>
        ))}
      </Box>
    );
  }

  // --- Standard (non-compact) mode ---

  // Loading state
  if (isLoading) {
    return (
      <Box className={styles.statsRow}>
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box className={styles.statsRow}>
        <Box className={styles.errorState}>
          <WarningIcon className={styles.errorIcon} />
          <Typography variant="body2" className={styles.errorText}>
            {error}
          </Typography>
          {onRetry && (
            <Typography
              variant="body2"
              className={styles.retryLink}
              onClick={onRetry}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRetry();
                }
              }}
            >
              Retry
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // No data state
  if (!stats) {
    return (
      <Box className={styles.statsRow}>
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No statistics available
          </Typography>
        </Box>
      </Box>
    );
  }

  // Format completed patrols as "X/Y"
  const completedDisplay = `${stats.patrolsCompletedToday}/${stats.patrolsTotalToday}`;

  return (
    <Box className={styles.statsRow}>
      <StatCard
        icon={<PersonIcon />}
        label="On Duty"
        value={stats.guardsOnDuty}
        color={theme.palette.success.main}
        statType="onDuty"
        isActive={activeFilter === 'onDuty'}
        onClick={onStatClick}
        tooltip="Guards on duty with active location (click to filter)"
      />
      <StatCard
        icon={<PlayArrowIcon />}
        label="Patrolling"
        value={stats.patrolsInProgress}
        color={theme.palette.info.main}
        statType="patrolling"
        isActive={activeFilter === 'patrolling'}
        onClick={onStatClick}
        tooltip="Guards actively patrolling right now (click to filter)"
      />
      <StatCard
        icon={<CheckCircleIcon />}
        label="Completed"
        value={completedDisplay}
        color={theme.palette.grey[500]}
        statType="completed"
        isActive={false}
        tooltip={`${stats.patrolsCompletedToday} of ${stats.patrolsTotalToday} scheduled patrols completed today (informational)`}
      />
      <StatCard
        icon={<WarningIcon />}
        label="Alerts"
        value={stats.activeAlerts}
        color={stats.activeAlerts > 0 ? theme.palette.error.main : theme.palette.grey[500]}
        statType="alerts"
        isActive={activeFilter === 'alerts'}
        onClick={onStatClick}
        tooltip={
          stats.activeAlerts > 0
            ? `${stats.activeAlerts} active alert${stats.activeAlerts !== 1 ? 's' : ''} requiring attention`
            : 'No active alerts'
        }
      />
    </Box>
  );
};

export default DispatchStatsRow;
